import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import LeadModal from '../components/LeadModal';
import { Shell, Spinner } from '../components/Shell';
import {
  API_BASE_URL, K, REPORT_FEE, GST, REPORT_TOTAL,
  apiFetch, leadBackoff, save, setToken, sleep,
} from '../lib/api';
import { dedupe, matchType, partialDate, searchUrl } from '../lib/records';

export default function ResultsPage() {
  const [params] = useSearchParams();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paying, setPaying] = useState(false);
  const [payErr, setPayErr] = useState('');
  const [modal, setModal] = useState(false);

  const d = params.get('d');
  const t = params.get('t');
  const v = params.get('v');
  const de = params.get('de') || '';
  const te = params.get('te') || '';
  const ve = params.get('ve') || '';
  const query = params.get('query');
  const propertyType = params.get('propertyType') || 'land';

  useEffect(() => {
    setLoading(true);
    fetch(searchUrl(API_BASE_URL, { d, t, v, district: '', taluka: '', village: '', query }))
      .then((r) => r.json())
      .then((data) => {
        if (data.error) setError(data.error);
        else setRecords(dedupe(data.results));
        setLoading(false);
      })
      .catch(() => { setError('Search failed'); setLoading(false); });
  }, [d, t, v, query]);

  const filtered = records.filter((r) => matchType(r, propertyType));

  const pay = async (lead) => {
    setPayErr('');
    setPaying(true);
    try {
      const ctx = { d, t, v, de, te, ve, query };
      save(K.records, filtered);
      save(K.ctx, ctx);

      const txnid = `ZMM_${Date.now()}_${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
      const payload = {
        email: lead.email,
        phone: lead.phone,
        district: de, taluka: te, village: ve, query, txnid,
        selectedKeys: filtered.map((r) => r.key).filter(Boolean),
        d, t, v, de, te, ve, propertyType,
      };

      let attempt = 0;
      let leadJson = {};
      for (;;) {
        let res;
        try {
          res = await apiFetch(`${API_BASE_URL}/leads`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
        } catch (_) {
          await sleep(leadBackoff(attempt++));
          continue;
        }
        leadJson = await res.json().catch(() => ({}));
        if (res.ok) break;
        if (res.status === 400) throw new Error(leadJson.error || 'Invalid details');
        await sleep(leadBackoff(attempt++));
      }
      if (leadJson.customerToken) setToken(leadJson.customerToken);

      const res = await fetch(`${API_BASE_URL}/payu/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: REPORT_TOTAL.toFixed(2),
          productinfo: `Report - ${query}`,
          firstname: 'Customer',
          email: lead.email,
          phone: lead.phone,
          searchQuery: query,
          txnid,
        }),
      });
      if (!res.ok) throw new Error('Payment could not start');
      const p = await res.json();
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = p.action;
      ['key', 'txnid', 'amount', 'productinfo', 'firstname', 'email', 'phone', 'surl', 'furl', 'hash', 'udf1'].forEach((f) => {
        const i = document.createElement('input');
        i.type = 'hidden';
        i.name = f;
        i.value = p[f] || '';
        form.appendChild(i);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (e) {
      setPayErr(e.message || 'Error');
      setPaying(false);
    }
  };

  const startPay = async () => {
    try {
      const me = await apiFetch(`${API_BASE_URL}/customer/session/me`);
      if (me.ok) {
        const j = await me.json();
        if (j.customer?.email && j.customer?.phone) {
          save(K.lead, { email: j.customer.email, phone: j.customer.phone });
          return pay({ email: j.customer.email, phone: j.customer.phone });
        }
      }
    } catch (_) {}
    setModal(true);
  };

  if (loading) return <Shell><Spinner /></Shell>;

  return (
    <Shell>
      <LeadModal
        open={modal}
        onClose={() => setModal(false)}
        onSubmit={async (lead) => { setModal(false); save(K.lead, lead); await pay(lead); }}
      />

      <div className="flex items-start justify-between gap-4 mb-6 animate-in">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-dark mb-1">Results</p>
          <h1 className="text-xl sm:text-2xl font-bold text-ink">
            {filtered.length} record{filtered.length !== 1 ? 's' : ''} found
          </h1>
          <div className="flex flex-wrap gap-1.5 mt-2">
            <span className="pill text-[11px]">{de}</span>
            <span className="pill text-[11px]">{te}</span>
            <span className="pill text-[11px]">{ve}</span>
            <span className="pill text-[11px] font-semibold text-brand-dark">#{query}</span>
          </div>
        </div>
        <Link to="/" className="btn-ghost text-sm shrink-0">New search</Link>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-600 text-sm px-4 py-3 mb-4">{error}</div>
      )}

      {!error && filtered.length === 0 && (
        <div className="card text-center py-12">
          <div className="mx-auto w-12 h-12 rounded-full bg-wash flex items-center justify-center text-muted mb-3 text-xl">∅</div>
          <p className="font-medium text-ink">No records found</p>
          <p className="text-sm text-muted mt-1">Try a different property number.</p>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="grid lg:grid-cols-5 gap-6 animate-in">
          <div className="lg:col-span-3">
            <ul className="card divide-y divide-line/80 p-0 overflow-hidden">
              {filtered.map((r, i) => (
                <li key={r.key || i} className="px-5 py-3.5 flex items-center gap-3 text-sm hover:bg-brand/[0.03] transition-colors">
                  <span className="h-2 w-2 rounded-full bg-gradient-to-br from-brand to-accent shrink-0" />
                  <span className="font-medium flex-1">{r.document_type || 'Record'}</span>
                  <span className="text-muted text-xs tabular-nums">
                    {partialDate(r.date) ? `${partialDate(r.date)}/····` : '—'}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted mt-2 pl-1">Document numbers unlock after payment.</p>
          </div>

          <div className="lg:col-span-2">
            <div className="card-accent sticky top-20 bg-gradient-to-b from-white to-brand/[0.03]">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted">Order</p>
              <p className="text-2xl font-bold text-ink mt-1">₹{REPORT_TOTAL}</p>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between text-muted">
                  <span>Report fee</span>
                  <span>₹{REPORT_FEE}</span>
                </div>
                <div className="flex justify-between text-muted">
                  <span>GST (18%)</span>
                  <span>₹{(REPORT_FEE * GST).toFixed(2)}</span>
                </div>
              </div>
              {payErr && <p className="text-red-500 text-xs mt-3">{payErr}</p>}
              <button type="button" onClick={startPay} disabled={paying} className="btn w-full mt-5">
                {paying ? 'Please wait…' : `Pay ₹${REPORT_TOTAL}`}
              </button>
              <p className="text-[11px] text-muted text-center mt-3">Secure checkout · HTML report included</p>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}
